import { type NavigationGuard } from "vue-router";
import { subscriptionsService } from "@shared/services/subscriptions";
import { useSubscriptionsStore } from "@shared/stores/subscriptions";
import { RouteNames } from "@shared/types/localTypes/router";
import { userGrantedToReachRoute } from "@/router/guard/security";
import { useBetaChecker } from "@/use/useBetaChecker";
import { useUpgradeStore } from "@shared/stores/upgrade";
import { stringLocalStorage } from "@shared/module-transformers/localStorage.ts";
import { LocalStorageKeys } from "@shared/types/localTypes/storage.ts";

export const checkForMultiSubscription: NavigationGuard = async () => {
  await subscriptionsService.loadInitialData();
  const subscriptions = useSubscriptionsStore().subscriptions;
  if (!subscriptions || subscriptions.length > 1) {
    return;
  }
  return {
    name: RouteNames.Dashboard,
    params: { subscriptionId: subscriptions[0].accountNumber },
  };
};

const getQueryParam = (queryParamString: string) => {
  const routeQueryParams = queryParamString.split("?");
  const urlSearchParams = new URLSearchParams(routeQueryParams[1] || "");
  return Object.fromEntries(urlSearchParams.entries());
};

export const checkFromCookie: NavigationGuard = async () => {
  const fromRoute = await stringLocalStorage.get(LocalStorageKeys.FROM_QUERY);
  await stringLocalStorage.remove(LocalStorageKeys.FROM_QUERY);
  if (fromRoute) {
    return {
      path: fromRoute,
      query: getQueryParam(fromRoute),
    };
  }
};

export const canAccessSubscription: NavigationGuard = async (to) => {
  await subscriptionsService.loadInitialData();
  const subscriptions = useSubscriptionsStore().subscriptions;
  if (!subscriptions) {
    throw new Error("unable to get subscriptions");
  }
  const targetSubscriptionId = to.params.subscriptionId as string;
  const subscriptionIdExists = subscriptions.some(
    (s) => s.accountNumber === parseInt(targetSubscriptionId),
  );
  if (!subscriptionIdExists) {
    return { name: RouteNames.Root };
  }
  if (!userGrantedToReachRoute(to)) {
    return {
      name: subscriptionsService.getFallbackRouteNameForSubscription(
        targetSubscriptionId,
      ),
      params: {
        subscriptionId: to.params.subscriptionId,
      },
    };
  }
};

export const setCurrentSubscription: NavigationGuard = async (to) => {
  const targetSubscriptionId = to.params.subscriptionId as string;
  await useSubscriptionsStore().setCurrentSubscription(targetSubscriptionId);
};

export const disableBetaIfNeeded: NavigationGuard = async () => {
  await useBetaChecker().disableBetaIfNeeded();
};

export const beforeEnterQuickUpgrade: NavigationGuard = async () => {
  await useUpgradeStore().setIsQuickUpgrade(true);
};

export const canReachDashboard: NavigationGuard = async (to) => {
  const subscriptions = useSubscriptionsStore().subscriptions;
  if (!subscriptions) {
    throw new Error("unable to get subscriptions");
  }
  const targetSubscriptionId = to.params.subscriptionId as string;
  const targetSubscription = subscriptions.find(
    (s) => s.accountNumber === parseInt(targetSubscriptionId),
  );
  if (!targetSubscription) {
    return { name: RouteNames.Root };
  }
  const meta = targetSubscription.meta;
  if (meta.isSaas && !meta.hasServers) {
    return {
      name: RouteNames.Topology,
      params: {
        subscriptionId: to.params.subscriptionId,
      },
    };
  }
};
